import type { QueryDSL, ValidationResult } from '../types/QueryTypes';
import { ALL_FIELDS } from '../metadata/fieldMetadata';
const SUPPORTED_OPERATORS = new Set(['=', '!=', '<', '<=', '>', '>=', 'contains', 'startsWith', 'endsWith', 'in', 'isNull']);
const SUPPORTED_AGGREGATES = new Set(['sum', 'avg', 'count', 'min', 'max']);
const VALID_DATASETS = new Set(['request', 'touchpoint', 'both']);
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({
    length: m + 1
  }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}
function findClosestField(field: string): string | null {
  const fieldNames = Object.keys(ALL_FIELDS);
  let best = '';
  let bestDist = Infinity;
  for (const f of fieldNames) {
    const dist = levenshtein(field.toLowerCase(), f.toLowerCase());
    if (dist < bestDist) {
      bestDist = dist;
      best = f;
    }
  }
  return bestDist <= 3 ? best : null;
}
function isFieldValidForDataset(field: string, dataset: string): boolean {
  const meta = ALL_FIELDS[field];
  if (!meta) {
    console.error('[NLQ] Field not in ALL_FIELDS:', field, '| ALL_FIELDS keys:', Object.keys(ALL_FIELDS).slice(0, 10));
    return false;
  }
  if (dataset === 'both') return true;
  const result = meta.datasets.includes(dataset);
  if (!result) {
    console.error('[NLQ] Field datasets mismatch:', field, '| meta.datasets:', meta.datasets, '| required:', dataset);
  }
  return result;
}
export function validateQuery(query: QueryDSL): ValidationResult {
  console.log('[NLQ] ALL_FIELDS region?', 'region' in ALL_FIELDS, '| keys count:', Object.keys(ALL_FIELDS).length);
  if (!query.dataset || !VALID_DATASETS.has(query.dataset)) {
    return {
      valid: false,
      error: `Unknown dataset '${query.dataset}'. Valid datasets: request, touchpoint, both`
    };
  }
  const dataset = query.dataset;
  if (query.filters) {
    for (const filter of query.filters) {
      if (!SUPPORTED_OPERATORS.has(filter.operator)) {
        return {
          valid: false,
          error: `Unknown operator '${filter.operator}'. Valid operators: ${[...SUPPORTED_OPERATORS].join(', ')}`
        };
      }
      if (filter.operator !== 'isNull' && !filter.field) {
        return {
          valid: false,
          error: 'Filter missing field name'
        };
      }
      if (filter.operator === 'isNull') continue;
      if (!isFieldValidForDataset(filter.field, dataset)) {
        const suggestion = findClosestField(filter.field);
        return {
          valid: false,
          error: `Unknown field '${filter.field}' for dataset '${dataset}'`,
          suggestion: suggestion ? `Did you mean '${suggestion}'?` : undefined
        };
      }
      if (filter.operator === 'in' && !Array.isArray(filter.value)) {
        return {
          valid: false,
          error: `Operator 'in' requires an array value`
        };
      }
    }
  }
  if (query.sort) {
    for (const sort of query.sort) {
      if (!isFieldValidForDataset(sort.field, dataset)) {
        const isAggResult = query.groupBy && query.aggregate?.some(a => sort.field === `${a.field}_${a.op}` && isFieldValidForDataset(a.field, dataset));
        if (!isAggResult) {
          const suggestion = findClosestField(sort.field);
          return {
            valid: false,
            error: `Unknown sort field '${sort.field}' for dataset '${dataset}'`,
            suggestion: suggestion ? `Did you mean '${suggestion}'?` : undefined
          };
        }
      }
      if (sort.direction !== 'asc' && sort.direction !== 'desc') {
        return {
          valid: false,
          error: `Sort direction must be 'asc' or 'desc', got '${sort.direction}'`
        };
      }
    }
  }
  if (query.groupBy && !isFieldValidForDataset(query.groupBy, dataset)) {
    const suggestion = findClosestField(query.groupBy);
    return {
      valid: false,
      error: `Unknown groupBy field '${query.groupBy}' for dataset '${dataset}'`,
      suggestion: suggestion ? `Did you mean '${suggestion}'?` : undefined
    };
  }
  if (query.aggregate) {
    for (const agg of query.aggregate) {
      if (!SUPPORTED_AGGREGATES.has(agg.op)) {
        return {
          valid: false,
          error: `Unknown aggregation '${agg.op}'. Valid: sum, avg, count, min, max`
        };
      }
      if (agg.op !== 'count' && !isFieldValidForDataset(agg.field, dataset)) {
        const suggestion = findClosestField(agg.field);
        return {
          valid: false,
          error: `Unknown aggregate field '${agg.field}' for dataset '${dataset}'`,
          suggestion: suggestion ? `Did you mean '${suggestion}'?` : undefined
        };
      }
    }
  }
  if (query.limit !== undefined && (typeof query.limit !== 'number' || query.limit < 0)) {
    return {
      valid: false,
      error: `Limit must be a non-negative number`
    };
  }
  return {
    valid: true
  };
}